/**
 * Adding a record to a register.
 *
 * This route existed and rendered `NotYet`, which is the one screen in the product a person
 * could reach and find nothing at.
 *
 * **A new record does not go straight in.** The design said it did, and the design was
 * wrong: `register_add` has no route of its own in `apps/api/seed/provisioning.py`, so it
 * takes the default four-tier chain exactly like an edit. That is consistent with the
 * architecture — every write to a governed table applies only on final approval, in the
 * transaction that records it — and this screen says so before somebody fills a form
 * expecting a row to appear.
 *
 * If the client does want new records to bypass approval, that is a route configuration and
 * a real decision, not a thing to imply with copy.
 *
 * The form is built from the register's own column catalogue, not from a hard-coded list of
 * fields. Eight registers with about a hundred and fifty columns between them, plus whatever
 * a tenant adds in the form builder — a screen that enumerated them would be wrong the first
 * time somebody published a field.
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import type { ColumnInfo, Envelope, PackageSummary, RegisterInfo } from '@/api/types'
import { navigate } from '@/app/routes'
import { Icon } from '@/components/Icon'
import { Failed, Skeleton } from '@/components/states'
import { registerLabel } from '@/lib/registers'

/** Columns a person can be asked to fill in.
 *
 * `editable` comes from the API, which derives it from the same `writable()` rule the
 * approval service enforces — so this offers exactly the fields an approval would accept.
 * Offering one it would refuse is how a governance tool loses trust in small increments. */
function askable(columns: ColumnInfo[]): ColumnInfo[] {
  return columns.filter((column) => column.editable && column.name !== 'locked')
}

export function NewEntry() {
  const { t } = useTranslation()
  const [packageId, setPackageId] = useState<string>('')
  const [slug, setSlug] = useState<string>('')
  const [values, setValues] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<string | null>(null)

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: ({ signal }) => api.get<RegisterInfo[]>('/registers', signal),
    staleTime: Infinity,
  })

  const packages = useQuery({
    queryKey: ['packages', 'all'],
    queryFn: ({ signal }) =>
      api.get<Envelope<PackageSummary>>('/packages?limit=200&sort=manual_package_id', signal),
  })

  const register = (registers.data ?? []).find((entry) => entry.slug === slug) ?? null
  const fields = register ? askable(register.columns) : []

  const raise = useMutation({
    mutationFn: () =>
      api.post<{ id: string; number: string }>('/applications', {
        application_type: 'register_add',
        title: t('create.title', {
          register: registerLabel(t, slug, register?.label ?? slug),
        }),
        package_id: packageId,
        entity_table: slug,
        // Blank fields are omitted rather than sent as empty strings. An empty string is a
        // value — it would read as "this was set to nothing" in the diff the approver sees,
        // which is a different statement from "this was left alone".
        payload: Object.fromEntries(
          Object.entries(values).filter(([, value]) => value.trim() !== ''),
        ),
      }),
    onSuccess: (created) => {
      setFailure(null)
      navigate({ name: 'application', applicationId: created.id })
    },
    onError: (caught) =>
      setFailure(caught instanceof ApiError ? caught.friendly : String(caught)),
  })

  if (registers.isError || packages.isError) {
    const error = registers.error ?? packages.error
    return (
      <main className="page">
        <Failed
          message={error instanceof ApiError ? error.friendly : String(error)}
          onRetry={() => {
            void registers.refetch()
            void packages.refetch()
          }}
        />
      </main>
    )
  }

  const ready = packageId !== '' && slug !== '' && fields.some((f) => values[f.name]?.trim())

  return (
    <main className="page" data-testid="new-entry">
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('create.heading')}</h1>
          <p className="page__subtitle">{t('create.subtitle')}</p>
        </div>
      </header>

      {/* Said before the form, not after the submit. Somebody who expects a row and gets an
          application has been surprised by their own governance tool. */}
      <p className="banner banner--locked" role="status" data-testid="approval-notice">
        <Icon name="lock" size={16} strokeWidth={1.8} />
        <span className="banner__text">{t('create.approvalNotice')}</span>
      </p>

      {registers.isPending || packages.isPending ? (
        <Skeleton rows={5} />
      ) : (
        <section className="card card--pad">
          <div className="field">
            <label className="field__label" htmlFor="new-package">
              {t('create.package')}
            </label>
            <select
              id="new-package"
              className="field__input"
              value={packageId}
              onChange={(event) => setPackageId(event.target.value)}
              data-testid="new-package"
            >
              <option value="">{t('create.choose')}</option>
              {(packages.data?.items ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.manual_package_id} — {entry.package_name ?? ''}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="new-register">
              {t('create.register')}
            </label>
            <select
              id="new-register"
              className="field__input"
              value={slug}
              onChange={(event) => {
                setSlug(event.target.value)
                // The fields belong to the register, so changing it discards them rather
                // than carrying a risk's score onto an issue.
                setValues({})
              }}
              data-testid="new-register"
            >
              <option value="">{t('create.choose')}</option>
              {(registers.data ?? []).map((entry) => (
                <option key={entry.slug} value={entry.slug}>
                  {registerLabel(t, entry.slug, entry.label)}
                </option>
              ))}
            </select>
          </div>

          {register ? (
            <div className="fields" data-testid="new-fields">
              {fields.map((column) => (
                <div className="field" key={column.name}>
                  <label className="field__label" htmlFor={`new-${column.name}`}>
                    {column.label}
                  </label>
                  {column.kind === 'longtext' ? (
                    <textarea
                      id={`new-${column.name}`}
                      className="field__input field__input--area"
                      rows={3}
                      value={values[column.name] ?? ''}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          [column.name]: event.target.value,
                        }))
                      }
                    />
                  ) : column.options.length > 0 ? (
                    <select
                      id={`new-${column.name}`}
                      className="field__input"
                      value={values[column.name] ?? ''}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          [column.name]: event.target.value,
                        }))
                      }
                    >
                      <option value="">{t('create.choose')}</option>
                      {column.options.map((option) => (
                        <option key={option.id} value={option.code}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={`new-${column.name}`}
                      className="field__input"
                      type={numeric(column.kind) ? 'number' : column.kind === 'date' ? 'date' : 'text'}
                      value={values[column.name] ?? ''}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          [column.name]: event.target.value,
                        }))
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          ) : null}

          {failure ? (
            <p className="banner banner--bad" role="alert" data-testid="create-error">
              <span className="banner__text">{failure}</span>
            </p>
          ) : null}

          <div className="actions">
            <button
              type="button"
              className="button button--primary"
              disabled={!ready || raise.isPending}
              onClick={() => raise.mutate()}
              data-testid="create-submit"
            >
              {t('create.submit')}
            </button>
            <button
              type="button"
              className="button button--quiet"
              onClick={() => navigate({ name: 'projects' })}
            >
              {t('create.cancel')}
            </button>
          </div>
        </section>
      )}
    </main>
  )
}

function numeric(kind: string): boolean {
  return kind === 'integer' || kind === 'number' || kind === 'percent' || kind === 'money'
}
